import { useIntl } from 'react-intl';
import moment from 'moment';
import { Col, DatePicker, Input, Row, Select } from 'antd';

import {
  CustomerType,
  ListWorkOrdersQuery,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { WorkOrderComplementTypeNumber } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';

import { ExtraFiltersProps } from '@/types/ExtraFiltersProps';
import OrderExtraFilter from '@/components/_common/ExtraFilters/OrderExtraFilter';
import {
  CompaniesInfiniteSelectField,
  InputDate,
  InputText,
} from '@/components/_common';
import { DateTimeFormat } from '@/utils/moment';
import MaskedInput from 'react-text-mask';
import { licensePlateMask } from '@/utils/mask/commonMasks';
import { isAppPortocar } from '@/index';

function WorkOrderListModalExtraFilters({
  queryParams,
  setQueryParams,
}: ExtraFiltersProps<ListWorkOrdersQuery>) {
  const intl = useIntl();

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <CompaniesInfiniteSelectField
          onChange={(values) => {
            setQueryParams((prev) => ({ ...prev, companiesIds: values }));
          }}
          value={queryParams?.companiesIds}
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.company',
          })}
          allowClear
          mode="multiple"
          queryFilter={{ rows: 50 }}
          queryEnable={!!queryParams?.companiesIds?.length}
        />
      </Col>

      <Col span={24} md={12}>
        <InputText
          onlyNumber
          value={queryParams?.number}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({
              ...prev,
              number: value,
            }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.number',
          })}
        />
      </Col>

      <Col span={24} md={12}>
        <Input
          value={queryParams?.estimator}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, estimator: value }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.estimator',
          })}
        />
      </Col>

      <Col span={24} md={12} lg={12} xxl={12}>
        <Input
          value={queryParams?.customer}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, customer: value }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.customer',
          })}
        />
      </Col>

      <Col span={24} md={12} lg={12} xxl={12}>
        <Input
          value={queryParams?.insurer}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, insurer: value }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.insurer',
          })}
        />
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <Select
          allowClear
          style={{ width: '100%' }}
          value={queryParams?.customerType}
          onChange={(value) =>
            setQueryParams((prev) => ({ ...prev, customerType: value }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.customerType',
          })}
          options={Object.values(CustomerType).map((item) => ({
            label: intl.formatMessage({ id: `workOrders.${item}` }),
            value: item,
          }))}
        />
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <Input
          value={queryParams?.vehicle}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, vehicle: value }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.vehicle',
          })}
        />
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <MaskedInput
          value={queryParams?.licensePlate}
          className="ant-input"
          mask={licensePlateMask}
          guide={false}
          name="licensePlate"
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({
              ...prev,
              licensePlate: value.toUpperCase(),
            }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.vehicle.licensePlate',
          })}
        />
      </Col>

      <Col span={24} md={12} lg={5} xxl={5}>
        <InputDate
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.estimatedLeaveDate',
          })}
          value={
            queryParams?.estimatedLeaveDate
              ? moment(queryParams?.estimatedLeaveDate)
              : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              estimatedLeaveDate: value?.format(DateTimeFormat),
            }))
          }
        />
      </Col>

      <Col span={24} md={12} lg={5} xxl={5}>
        <InputDate
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.entryDate',
          })}
          value={
            queryParams?.entryDate ? moment(queryParams?.entryDate) : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              entryDate: value?.format(DateTimeFormat),
            }))
          }
        />
      </Col>

      <Col span={24} md={12} lg={5} xxl={5}>
        <InputDate
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.authorizationDate',
          })}
          value={
            queryParams?.authorizationDate
              ? moment(queryParams?.authorizationDate)
              : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              authorizationDate: value?.format(DateTimeFormat),
            }))
          }
        />
      </Col>

      <Col span={24} md={12} lg={5} xxl={5}>
        <InputDate
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.closingDate',
          })}
          value={
            queryParams?.closingDate
              ? moment(queryParams?.closingDate)
              : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              closingDate: value?.format(DateTimeFormat),
            }))
          }
        />
      </Col>

      <Col span={24} md={12} lg={4} xxl={4}>
        <DatePicker
          format="DD/MM/YYYY"
          style={{ width: '100%' }}
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.returnDate',
          })}
          value={
            queryParams?.returnDate
              ? moment(queryParams?.returnDate)
              : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              returnDate: value?.format(DateTimeFormat),
            }))
          }
        />
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <Select
          allowClear
          style={{ width: '100%' }}
          value={queryParams?.type}
          onChange={(value) =>
            setQueryParams((prev) => ({ ...prev, type: value }))
          }
          placeholder={intl.formatMessage({
            id: 'workOrders.domain.complements.type',
          })}
          mode="multiple"
        >
          <Select.Option value={WorkOrderComplementTypeNumber.Courtesy}>
            {intl.formatMessage({
              id: `workOrders.domain.enums.work_order_type.${
                isAppPortocar ? 'insideCourtesy' : 'courtesy'
              }`,
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementTypeNumber.Warranty}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_type.warranty',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementTypeNumber.WorkOrder}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_type.work_order',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementTypeNumber.TotalLoss}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_type.total_loss',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementTypeNumber.Return}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_type.return',
            })}
          </Select.Option>

          <Select.Option value={WorkOrderComplementTypeNumber.Revision}>
            {intl.formatMessage({
              id: 'workOrders.domain.enums.work_order_type.revision',
            })}
          </Select.Option>
        </Select>
      </Col>

      <Col span={24} md={8} lg={8} xxl={8}>
        <OrderExtraFilter
          optionsValue={{ asc: 0, desc: 1 }}
          value={queryParams?.order}
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              order: value,
            }))
          }
        />
      </Col>
    </Row>
  );
}

export default WorkOrderListModalExtraFilters;
